import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import type { EntryIntentRequest, HouseholdPetBrief, PetOrigin, PetSpecies, SessionState, SettingsUpdateInput, SettingsView } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { onboardingRoute, routeAfterSession, useSessionState } from "@/shared/session/onboarding";
import { useCurrentHousehold } from "@/shared/session/householdContext";
import { serverNow } from "@/shared/time/clock";
import { Button, Card, DisabledState, ErrorState, Icon, LoadingState, Page, PetAvatar, ToggleChip, TopBar, avatarChain, speciesArtLabel, useAvatarStep } from "@/shared/ui";
import { EntryHeading, EntrySteps, entryStepKicker } from "./EntryHeading";
import { BrandLogo } from "@/shared/ui/BrandLogo";
import "./identity.css";
import entryFilm from "./assets/entry-film-mobile.mp4";
import entryPoster from "./assets/entry-film-poster.jpg";
import invitationLetter from "@/features/pets/assets/entry-invitation-letter-v1.webp";
import courtyard from "@/features/home/assets/living/courtyard-base.webp";
import { PawBadge } from "@/features/pets/PetPortrait";
import { ResidentPortrait } from "@/features/pets/ResidentPortrait";
import { sizedPhotoSrc, usePhotoSizes } from "@/shared/ui/photoSizes";
import { SpeciesIllustration } from "@/features/pets/SpeciesIllustration";
import { petToReturnTo } from "@/features/world_map/WorldGate";
import { MoveInAway, backAtOf, forgetAway, readAway, rememberAway, useAwayView, useMoveInWhileAway, type AwayWait } from "./MoveInAway";
import { CHAT_DATA_USE, DataUseLink } from "./dataUse/DataUseNote";
import { BlockedSection } from "./BlockedSection";
import { RomanceSetting } from "./RomanceSetting";
import { AudienceSummary } from "./AudienceSummary";
import { CityPicker, cityBlocked, useCityStatus, type CityPick } from "./CityPicker";

export function WelcomePage() {
  const [params] = useSearchParams();
  const guest = params.get("view") === "guest";
  const { session: sessionService } = useServices();
  const session = useQuery({ queryKey: queryKeys.session, queryFn: () => sessionService.current(), enabled: !guest, staleTime: 30_000 });
  const film = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setReduceMotion(preference.matches);
      if (preference.matches) film.current?.pause();
    };
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);
  if (!guest && env.dataMode === "live" && session.isSuccess && session.data?.authenticated) return <Navigate to={routeAfterSession(session.data)} replace />;
  return (
    <Page bare className="ps-welcome-page">
      <div className="ps-welcome-film" aria-hidden="true">
        <img src={entryPoster} alt="" />
        <video ref={film} src={entryFilm} poster={entryPoster} autoPlay={!reduceMotion} muted playsInline loop preload="metadata" />
      </div>
      <div className="ps-welcome-top">
        <BrandLogo size="welcome" />
        {!reduceMotion ? <button
          type="button"
          className="ps-welcome-pause"
          aria-label={paused ? "播放开场影片" : "暂停开场影片"}
          onClick={() => {
            const video = film.current;
            if (!video) return;
            if (video.paused) {
              void video.play().then(() => setPaused(false)).catch(() => setPaused(true));
            } else {
              video.pause();
              setPaused(true);
            }
          }}
        >
          {paused ? "▶" : "Ⅱ"}
        </button> : null}
      </div>
      <div className="ps-welcome-bottom">
        <span className="ps-welcome-eyebrow">欢迎来到 PetSoul 星球</span>
        <h1>和 TA 一起，<br />走进另一个世界。</h1>
        <p>每只宠物，都有一段属于自己的故事。</p>
        <div className="ps-welcome-actions">
          <Link to="/register" className="ps-welcome-action ps-welcome-action--primary">寻找我的 TA <span aria-hidden="true">↗</span></Link>
          <Link to="/world?view=guest" className="ps-welcome-action ps-welcome-action--secondary">跳过，先看看这个世界 <span aria-hidden="true">→</span></Link>
        </div>
        <Link to="/login" className="ps-welcome-login">已经找到 TA 了？登录</Link>
        <span className="ps-welcome-disclosure">开场影片为概念影像，不代表你或居民的真实宠物。</span>
      </div>
    </Page>
  );
}

function entryFromParams(params: URLSearchParams): EntryIntentRequest | null {
  const kind = params.get("entry");
  if (kind === "adopt") {
    const petId = params.get("pet_id");
    return petId ? { kind, pet_id: petId, invite_token: null } : null;
  }
  if (kind === "invite") {
    const token = params.get("invite");
    return token ? { kind, pet_id: null, invite_token: token } : null;
  }
  if (kind === "browse" || kind === "own_pet") return { kind, pet_id: null, invite_token: null };
  return null;
}

function SelectedResidentBanner({ petId }: { petId: string }) {
  const { pets } = useServices();
  const selected = useQuery({ queryKey: queryKeys.publicPet(petId), queryFn: () => pets.publicPet(petId), retry: false, staleTime: 30_000 });
  if (selected.isPending) return <p className="ps-selected-resident ps-selected-resident--loading">正在找回刚才认识的居民…</p>;
  if (selected.isError || !selected.data) return <p className="ps-selected-resident">这位居民暂时无法打开，<Link to="/world">返回星球看看</Link>。</p>;
  // 头像和星球居民卡、居民主页同一套（ResidentPortrait 的退回链，2026-09-27 D4）：官方形象照 → TA 原来那张照片 → 同物种插画，
  // 图打不开自动往下退一级、读屏跟着换，不留破图，也不写名字首字。照片地址与“官方形象”的认法同居民主页（ResidentPage）；
  // 来历、原来那张照片只有居民那一栏有（公开主页的 profile 不带），没有就不给。
  // 这条在注册 / 登录的纸色表单里（昼夜都是暖纸）：头像按纸面画（surface="paper"），深色下底块、“插画”小标不变成深色块。
  const { profile, resident } = selected.data;
  const photo = profile.avatar_url ?? resident?.avatar_url ?? null;
  const official = resident?.official_avatar === true && photo === (resident?.avatar_url ?? null);
  return (
    <div className="ps-selected-resident">
      <ResidentPortrait name={profile.display_name} species={profile.species} photoUrl={photo} fallbackUrl={resident?.fallback_avatar_url ?? null} official={official} origin={resident?.origin ?? null} size={45} surface="paper" />
      <span><small>刚才认识的居民</small><strong>{profile.display_name}</strong></span>
      <Link to={`/world/residents/${encodeURIComponent(petId)}`}>再看一眼</Link>
    </div>
  );
}

function SelectedInviteBanner({ token }: { token: string }) {
  const { households } = useServices();
  const invite = useQuery({ queryKey: queryKeys.invitePreview(token), queryFn: () => households.previewInvite(token), retry: false, staleTime: 0 });
  if (invite.isPending) return <p className="ps-selected-resident ps-selected-resident--loading">正在核对家人邀请…</p>;
  if (invite.isError || !invite.data) return <p className="ps-selected-resident">这封邀请暂时无法打开。请检查链接或稍后重试。</p>;
  return <div className="ps-selected-resident">
    <span className="ps-selected-resident__portrait"><img src={invitationLetter} alt="" /></span>
    <span><small>刚才打开的家人邀请</small><strong>{invite.data.household_name || "一个正在生活的家"}</strong></span>
    <Link to={`/join?invite=${encodeURIComponent(token)}`}>再看一眼</Link>
  </div>;
}

function AuthForm({ kind }: { kind: "register" | "login" }) {
  const { session } = useServices();
  // 注册页那句找回（AUTH-RECOVERY-UI-116，I 10-16）：能力表说能找回才换说法；读不到、没开、还在读、演示模式都照旧提醒记住密码。登录页不读
  const capabilities = useQuery({
    queryKey: queryKeys.authCapabilities,
    queryFn: () => session.authCapabilities!(),
    enabled: kind === "register" && typeof session.authCapabilities === "function",
    staleTime: 60_000,
    retry: false,
  });
  const canRecover = capabilities.isSuccess && capabilities.data.recovery_available === true;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const entry = entryFromParams(params);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const submit = useMutation({
    mutationFn: () => (kind === "register" ? session.register(username.trim(), password, undefined, entry) : session.login(username.trim(), password)),
    onSuccess: (state: SessionState) => {
      // 新会话：丢弃上一个账号的全部缓存，再按入住阶段跳转。
      queryClient.clear();
      queryClient.setQueryData(queryKeys.session, state);
      // Existing accounts have no register(entry) call: carry the selected public resident through login URL.
      const chosenPet = kind === "login" && entry?.kind === "adopt" ? entry.pet_id : null;
      const inviteToken = entry?.kind === "invite" ? entry.invite_token : null;
      navigate(inviteToken ? `/join?invite=${encodeURIComponent(inviteToken)}` : chosenPet && state.onboarding?.step === "needs_companion" ? `/onboarding/choice?pet_id=${encodeURIComponent(chosenPet)}` : routeAfterSession(state), { replace: true });
    },
  });
  const error = submit.error ? toApiError(submit.error) : null;
  return (
    <form
      className="ps-stack ps-auth-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <div className="ps-field">
        <label htmlFor="username">你的账号名</label>
        <input
          id="username"
          className="ps-input"
          autoComplete="username"
          autoCapitalize="none"
          placeholder="用来找到属于你的家"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          minLength={3}
          maxLength={32}
          pattern="[A-Za-z0-9_.\-]{3,32}"
          title="3–32 位字母、数字、下划线、点或短横线"
          aria-describedby={kind === "register" ? "username-note" : undefined}
          required
        />
      </div>
      <div className="ps-field">
        <label htmlFor="password">{kind === "register" ? "设置密码" : "密码"}</label>
        <div className="ps-auth-password">
          <input
            id="password"
            className="ps-input"
            type={showPassword ? "text" : "password"}
            autoComplete={kind === "register" ? "new-password" : "current-password"}
            placeholder={kind === "register" ? "至少 8 位" : "输入密码"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={kind === "register" ? 8 : 1}
            maxLength={128}
            aria-describedby={kind === "register" ? "password-note" : undefined}
            required
          />
          <button type="button" className="ps-auth-password__toggle" aria-label={showPassword ? "隐藏密码" : "显示密码"} aria-pressed={showPassword} onClick={() => setShowPassword((old) => !old)}>
            {showPassword ? "隐藏" : "显示"}
          </button>
        </div>
      </div>
      <Button type="submit" variant="primary" block loading={submit.isPending}>
        {kind === "register" ? "创建账号，继续" : "登录，继续"}
      </Button>
      {error ? (
        error.isCapabilityUnavailable ? (
          <DisabledState title="账号注册/登录不可用">{error.message}</DisabledState>
        ) : error.code === "USERNAME_TAKEN" || error.code === "INVALID_CREDENTIALS" || error.code === "RATE_LIMITED" || error.code === "VALIDATION_FAILED" ? (
          <p role="alert" className="ps-form-error">
            {error.code === "VALIDATION_FAILED" ? "用户名或密码格式不对。" : error.message}
          </p>
        ) : (
          <ErrorState error={error} />
        )
      ) : null}
      {/* 两句字段说明挪到主按钮下面（UI-BASE-01b，W-07：320×568 上主按钮要在首屏整颗露出来）；输入框用 aria-describedby 连着，读屏照样读到。
          只在注册时给：登录时「登录，继续」下面不写注册规则（UI-REVIEW110-01） */}
      {kind === "register" ? (
        <div className="ps-auth-notes">
          <p id="username-note" className="ps-auth-field-note">3–32 位，可用英文字母、数字、点、下划线或短横线。</p>
          {/* 开了也照实说：账号名注册的账号现在绑不了邮箱、手机号（绑定页在 118 第一波），没绑定过的照样找不回 */}
          <p id="password-note" className="ps-auth-field-note">{canRecover ? "忘了密码时，可以用绑定过的邮箱或手机号找回；没绑定过的，请记住这把钥匙。" : "现在还没有密码找回功能，请记住这把钥匙。"}</p>
        </div>
      ) : null}
    </form>
  );
}

export function RegisterPage() {
  const [params] = useSearchParams();
  const entry = entryFromParams(params);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const back = entry?.kind === "invite" && entry.invite_token ? `/join?invite=${encodeURIComponent(entry.invite_token)}` : entry?.kind === "adopt" && entry.pet_id ? `/world/residents/${encodeURIComponent(entry.pet_id)}` : "/welcome";
  return (
    <Page bare className={`ps-entry-page ps-auth-page${entry?.kind === "adopt" ? " ps-auth-page--adopt" : ""}`}>
      <div className="ps-auth-hero">
        <TopBar title="注册" heading={false} back={back} right={<Link to="/world?view=guest" className="ps-btn ps-btn--ghost ps-auth-skip" aria-label="先逛逛：跳过，先看看这个世界">先逛逛</Link>} />
        <div className="ps-auth-hero__caption">
          <BrandLogo />
          <strong>和 TA 一起，走进另一个世界。</strong>
        </div>
      </div>
      <div className="ps-auth-sheet">
        <EntryHeading step={1} kicker="第一站 · 建立账号" title={entry?.kind === "invite" ? "先收好这封邀请" : entry?.kind === "adopt" ? "把刚才的相遇留住" : "给自己留一把钥匙"} description={entry?.kind === "invite" ? "建立账号后，你可以决定是否加入这个家。" : entry?.kind === "adopt" ? "建立账号后，回到刚才认识的居民身边。" : "先认识你，再带 TA 来到这个世界。"} />
        {entry?.kind === "adopt" && entry.pet_id ? <SelectedResidentBanner petId={entry.pet_id} /> : null}
        {entry?.kind === "invite" && entry.invite_token ? <SelectedInviteBanner token={entry.invite_token} /> : null}
        <AuthForm kind="register" />
        <p className="ps-entry-support">创建账号不会自动领养居民或加入家庭，之后仍由你确认。</p>
        <p className="ps-entry-support ps-auth-switch">已有账号？<Link to={`/login${suffix}`}>登录，继续</Link></p>
      </div>
    </Page>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const entry = entryFromParams(params);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const back = entry?.kind === "invite" && entry.invite_token ? `/join?invite=${encodeURIComponent(entry.invite_token)}` : entry?.kind === "adopt" && entry.pet_id ? `/world/residents/${encodeURIComponent(entry.pet_id)}` : "/welcome";
  return (
    <Page bare className={`ps-entry-page ps-auth-page${entry?.kind === "adopt" ? " ps-auth-page--adopt" : ""}`}>
      <div className="ps-auth-hero">
        <TopBar title="登录" heading={false} back={back} right={<Link to="/world?view=guest" className="ps-btn ps-btn--ghost ps-auth-skip" aria-label="先逛逛：跳过，先看看这个世界">先逛逛</Link>} />
        <div className="ps-auth-hero__caption">
          <BrandLogo />
          <strong>另一个世界，等你回来。</strong>
        </div>
      </div>
      <div className="ps-auth-sheet">
        <EntryHeading kicker="欢迎回来" title="继续你们的故事" description="从上次停下的地方继续。" />
        {entry?.kind === "adopt" && entry.pet_id ? <SelectedResidentBanner petId={entry.pet_id} /> : null}
        {entry?.kind === "invite" && entry.invite_token ? <SelectedInviteBanner token={entry.invite_token} /> : null}
        <AuthForm kind="login" />
        <p className="ps-entry-support ps-auth-switch">第一次来？<Link to={`/register${suffix}`}>创建账号</Link></p>
      </div>
    </Page>
  );
}

/** 入住失败的每种原因都有自己的出路；pet_away 是等待，不是错误。 */
function moveInProblem(error: unknown): { reason: string | null; message: string } | null {
  if (!error) return null;
  const err = toApiError(error);
  const reason = typeof err.details?.reason === "string" ? err.details.reason : null;
  if (reason === "pet_away") return { reason, message: "" };
  if (reason === "habitat_not_supported") return { reason, message: "这座城还没有这一类生活环境，已经换成城市生活，可以再点一次入住。" };
  if (reason === "region_unsupported") return { reason, message: "这个地区暂时还没开放，换一座城吧。" };
  if (reason === "city_required") return { reason, message: "先选一座城，才能入住。" };
  if (reason === "manage_required") return { reason, message: "家的位置由家庭管理员决定。请管理员先选好城市，带 TA 入住。" };
  if (err.kind === "network" || err.kind === "timeout" || (err.status ?? 0) >= 500) return { reason: "unconfirmed", message: "入住结果还没确认。已经重新核对；如果还停在这里，可以再点一次，不会重复入住。" };
  return { reason, message: err.message };
}

/**
 * 入住页头图里门廊上的 TA：和全站头像同一条退回链（shared/ui 的 avatarChain，2026-09-27 D4 第二批）——
 * TA 公开主页上的照片 → 演示小灰猫（只在演示模式）→ 物种插画（只给领养来的原创居民：来历是入住阶段里这只的 pet_origin，
 * 物种是公开主页上的）或爪印；照片打不开就往下退，读屏跟着换。底下那行小字：照片写名字；插画、爪印写“暂无照片”，
 * 照片打不开时写“照片暂时打不开”（和读屏同一句；TA 其实有照片，不说没有。2026-09-27 D4 第三批从“照片打不开”改来）。
 * 矮屏（identity.css 的 max-height: 780px 段）相框缩到 60–80px，这句一行放不下：放一长一短两句，由那一段切换显示——
 * 矮屏显示短的“照片打不开”（一行放得下），其余高度照旧显示“照片暂时打不开”（2026-09-27 D4，6c2b 定）。两句都 aria-hidden，
 * 读屏只念相框的 aria-label（“{名字}，照片暂时打不开”；没有照片“{名字}，暂无照片”，全站一套说法，UI-BASE-01b），不因屏幕高矮而变。相框是 role="img"：它的 aria-label 才会被读屏念出来
 * （没有角色的 span 上的 aria-label 读屏可以不念），里面的图和小字只是画面。
 */
function MoveInHeroPet({ name, photoUrl, origin, species }: { name: string; photoUrl: string | null; origin: PetOrigin | null | undefined; species: PetSpecies | undefined }) {
  const { step, photoFailed, next } = useAvatarStep(avatarChain(photoUrl, origin, species));
  // 照片缩略档（UI-PET-PHOTO-SIZES-02）：相框最大 112px（identity.css 的 clamp），取 384 那档；没有这项能力时照旧原图
  const sized = usePhotoSizes();
  const pic = step.kind === "photo" || step.kind === "demo" ? step.src : null;
  const art = step.kind === "species" ? step.species : null;
  const label = pic ? `${name}的当前头像` : art ? speciesArtLabel(art, name) : photoFailed ? `${name}，照片暂时打不开` : `${name}，暂无照片`;
  return (
    <span className={`ps-movein-hero__pet${pic ? " has-photo" : art ? " is-species" : ""}`} role="img" aria-label={label} data-species={art ?? undefined}>
      {pic ? <img key={pic} src={sized ? sizedPhotoSrc(pic, 112) : pic} alt="" onError={next} /> : art ? <SpeciesIllustration species={art} /> : <PawBadge size={72} />}
      <small>
        {pic ? (
          name
        ) : photoFailed ? (
          <>
            <span className="ps-movein-hero__long-note" aria-hidden="true">照片暂时打不开</span>
            <span className="ps-movein-hero__short-note" aria-hidden="true">照片打不开</span>
          </>
        ) : (
          "暂无照片"
        )}
      </small>
    </span>
  );
}

/** 入住激活：与接待分开。主人明确选择是否让 TA 的旅行到访生成公开动态（默认不公开）。 */
export function MoveInPage() {
  const { session, pets } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const state = useSessionState();
  const [publicPosts, setPublicPosts] = useState(false);
  // 入住选城（UI-MOVEIN-CITY-01）：选中的那座城和这座城里的生活环境；没有默认城市，不选不能入住
  const [pick, setPick] = useState<CityPick | null>(null);
  // 入住成功后本页不再按会话步骤自行跳转：否则守卫的 <Navigate replace> 会盖掉带“到家时刻”状态的那次跳转。
  const movedIn = useRef(false);
  const petId = state.data?.onboarding?.pet_id ?? null;
  const userId = state.data?.user?.user_id ?? null;
  // 等 TA 回来（409 pet_away）：记下是哪一只、计划几点回到驿站；去星球逛一圈、被守卫带回来时还是这一份（MoveInAway.tsx）。
  // “领养了在外面的居民也能先入住”开着时，入住直接成功：标签页里记着的旧等待不再认，并清掉（巡检 R9-01）；
  // 这时真收到 409 pet_away（服务端说了算）照样进等待。开关还没读到时先不认也不清。
  const whileAway = useMoveInWhileAway();
  const [away, setAway] = useState<AwayWait | null>(null);
  useEffect(() => {
    if (!userId || whileAway === "unknown") return;
    if (whileAway === "on") forgetAway(userId);
    else setAway((known) => known ?? readAway(userId));
  }, [userId, whileAway]);
  const profile = useQuery({ queryKey: queryKeys.petProfile(petId ?? "-"), queryFn: () => pets.publicProfile(petId!), enabled: Boolean(petId) });
  const place = useQuery({ queryKey: queryKeys.homePlace(petId ?? "-"), queryFn: () => session.homePlace(petId), enabled: Boolean(petId), retry: false });
  const cityStatus = useCityStatus(pick?.cityId);
  // 家第一次入住、还没定过地方：要主人选城（后端 city_required）。家已经住着人（后加的宠物）或者已经定过地方：住进同一处，不带城
  const firstChoice = Boolean(place.data && !place.data.place.chosen && !state.data?.onboarding?.home_activated_at);
  const moveIn = useMutation({
    mutationFn: () => (firstChoice && pick ? session.moveInCity(publicPosts, pick.cityId, pick.habitat, petId) : session.moveIn(publicPosts, null, petId)),
    onSuccess: (onboarding) => {
      movedIn.current = true;
      setAway(null);
      forgetAway(userId);
      queryClient.setQueryData<SessionState | undefined>(queryKeys.session, (prev) => (prev ? { ...prev, onboarding } : prev));
      void queryClient.invalidateQueries({ queryKey: queryKeys.home });
      void queryClient.invalidateQueries({ queryKey: ["households"] });
      // The choice is user-scoped and revalidated against /households before display.
      if (userId && petId) {
        try { sessionStorage.setItem(`petsoul:current-pet:${userId}`, petId); } catch { /* still usable without storage */ }
      }
      // 到家时刻只跟着这一次跳转（路由状态）出现：刷新、返回都不会重播。
      // 这里有意去小窝 /home、不去地图首页 /map：第一次入住的“到家时刻”在小窝里播（小窝左上角再回地图）；
      // 其余完成、返回都已改去 /map 或“我的”（2026-09-24 新导航）。
      // 10-03 起先去到家等待页（用户：等 TA 的样子都好了再到家，不要先看到只是贴图）；等待页核完整了才带着 arrival 进小窝（c84a D0812-1）
      navigate("/arriving", { replace: true, state: petId ? { arrival: petId } : null });
    },
    onError: (error) => {
      const problem = moveInProblem(error);
      if (problem?.reason === "pet_away" && petId) {
        // 还在外面：按这一次给的 back_at 等；点了又回 409 就换成新的那一份。到点也不替主人发入住。
        const wait: AwayWait = { petId, backAt: backAtOf(toApiError(error).details), seenAt: serverNow() };
        setAway(wait);
        rememberAway(userId, wait);
        return;
      }
      // 别的原因说明已经不是“在外面”了（入住接口先查在不在外面）；只有回执不确定时还接着等。
      if (problem && problem.reason !== "unconfirmed") {
        setAway(null);
        forgetAway(userId);
      }
      // 这座城没有选的那一类环境：换回人人都有的城市生活，城不换；地区没开放、不是管理员：清掉这座城；前两种重读这座城的实况
      if (problem?.reason === "habitat_not_supported") setPick((known) => (known ? { ...known, habitat: "city" } : known));
      if (problem?.reason === "region_unsupported" || problem?.reason === "manage_required") setPick(null);
      if (problem?.reason === "habitat_not_supported" || problem?.reason === "region_unsupported") void cityStatus.refetch();
      // 入住接口幂等：回执不确定时先重读会话；若其实已经入住，下方守卫会按入住阶段直接带去地图首页。
      if (problem?.reason === "unconfirmed") void state.refetch();
    },
  });
  // 在等的就是这一页的这只（换了宠物、已经住进来就不算）；到点只换主按钮，不自动发入住。
  const waiting = away && petId && away.petId === petId && !movedIn.current ? away : null;
  const awayNow = useAwayView(waiting);
  const due = Boolean(awayNow?.due);

  if (env.dataMode === "fixture") return <Navigate to="/map" replace />;
  if (state.isPending) return <LoadingState lines={2} />;
  if (state.isError) return <ErrorState error={state.error} onRetry={() => void state.refetch()} />;
  if (!state.data.authenticated) return <Navigate to="/welcome" replace />;
  if (movedIn.current) return <Page bare className="ps-entry-page ps-movein-page"><LoadingState lines={1} label="正在回家…" /></Page>;
  const step = state.data.onboarding?.step;
  if (step !== "ready_to_move_in" && step !== "reception_optional") return <Navigate to={onboardingRoute(state.data.onboarding)} replace />;

  const name = profile.data?.display_name ?? "TA";
  const problem = moveIn.isError && !moveIn.isPending ? moveInProblem(moveIn.error) : null;
  const view = place.data;
  // 第一次入住还没选城、或者选的城是暂不开放的地区：入住按钮先别让点，贴底区写一句为什么
  const needCity = firstChoice && (!pick || cityBlocked(cityStatus.data, cityStatus.error));
  // 账号里已经有住进来的宠物、这一只是后加的：给一个“先不加了”的出口回地图，当前宠物还是原来那只（第一次入住没有这个出口）。
  const returnTo = petToReturnTo(state.data.onboarding, state.data.user?.user_id);
  return (
    <Page bare className="ps-entry-page ps-movein-page">
      <TopBar title="入住" heading={false} subtitle="最后一步，带 TA 回家" />
      {/* 等 TA 回来时（巡检 R8-A06）：说明放在这张卡里、不吸底；TA 还没到家门口，不放庭院头图和门廊上的 TA */}
      <section className={`ps-movein-hero${waiting ? " is-waiting" : ""}`} aria-label={`${name} 的新家`}>
        {waiting ? null : (
          <>
            <img className="ps-movein-hero__scene" src={courtyard} alt="" />
            <MoveInHeroPet name={name} photoUrl={profile.data?.avatar_url ?? null} origin={state.data.onboarding?.pet_origin} species={profile.data?.species} />
          </>
        )}
        <div className="ps-movein-hero__caption">
          <span className="ps-movein-hero__kicker">{entryStepKicker(4)}</span>
          <h1>带 {name} 回家</h1>
          {waiting ? <MoveInAway name={name} wait={waiting} phrase={awayNow?.phrase ?? null} due={due} /> : <p>入住之后，家园、手机和 TA 的旅途会按真实时间慢慢展开。</p>}
          <EntrySteps step={4} />
        </div>
      </section>

      <section className="ps-movein-section" aria-labelledby="movein-place-title">
        <h2 id="movein-place-title">{firstChoice ? `${name} 在哪里开始生活？` : "家的样子"}</h2>
        {place.isPending ? <LoadingState lines={1} label="正在确认可以安家的地方…" /> : place.isError ? <ErrorState error={place.error} onRetry={() => void place.refetch()} /> : view ? (
          firstChoice ? (
            <>
              <p className="ps-movein-note">选一座城，{name} 就从那里开始生活。新家落在那座城真实的街区里，具体住处只给家里人看。</p>
              <CityPicker petId={petId} pick={pick} onPick={setPick} status={cityStatus} />
            </>
          ) : (
            // 老家还没选过城市（chosen=false，B-CITY-DEFAULT-01：display 是「还没选城市」一类的说法，不是一个地方）：不拼进「这个家在…」，只说住进同一个家（UI-MOVEIN-CITY-01b）；
            // 地址还在落定（address_pending）：如实说落定前先不出门，和选城卡上那句同一个意思
            <p className="ps-movein-note">{view.place.chosen ? `这个家在 ${view.place.display}，${name} 会住进同一处。` : `${name} 会住进同一个家。`}{view.place.address_pending ? "新家地址还在落定，落定之前先待在家里，不出门。" : null}</p>
          )
        ) : null}
      </section>

      <section className="ps-movein-section">
        <label className="ps-switch-row">
          <span>
            <strong>让 TA 的旅途见闻（含途中照片）出现在朋友圈</strong>
            <small>默认关闭。只在 TA 真实到访之后才会发；你们的私聊和生活叮嘱永远不会公开。之后可以在设置里改。</small>
          </span>
          <input type="checkbox" role="switch" className="ps-switch" checked={publicPosts} onChange={(e) => setPublicPosts(e.target.checked)} />
        </label>
      </section>

      {/* 第二只起的出口放在正文最后（吸底区只留主按钮，2026-09-25 主窗口定）：去地图，当前宠物还是原来那只（第一次入住没有这个出口） */}
      {returnTo ? (
        <p className="ps-movein-exit">
          <Link to="/map">
            <Icon name="back" size={16} />
            先不加了，回到 {returnTo.name}
          </Link>
        </p>
      ) : null}

      <div className="ps-entry-dock" role="group" aria-label="入住">
        {problem && problem.reason !== "pet_away" ? <p role="alert" className="ps-form-error ps-entry-dock__error">{problem.message}</p> : null}
        {needCity ? <p className="ps-entry-dock__hint" id="movein-city-need">{pick ? "这个地区暂时还没开放，换一座城吧。" : "先选一座城，才能入住。"}</p> : null}
        {/* 等的时候：没到点是次按钮“再看看”，到点（back_at）才把“接 TA 回家”变成主按钮，由主人点 */}
        <Button
          variant={waiting && !due ? "secondary" : "primary"}
          block
          icon="home"
          loading={moveIn.isPending}
          disabled={place.isPending || place.isError || needCity}
          aria-describedby={needCity ? "movein-city-need" : undefined}
          onClick={() => moveIn.mutate()}
        >
          {waiting ? (due ? "接 TA 回家" : "再看看 TA 回来没有") : "入住，一起开始生活"}
        </Button>
      </div>
    </Page>
  );
}

const VISIBILITY: Array<{ id: string; label: string }> = [
  { id: "public", label: "所有人" },
  { id: "followers", label: "关注 TA 的宠物" },
  // 「只有我」不准：私密时家里的人都看得到（PRIVACY-COPY-01）
  { id: "private", label: "只有家人" },
];

/**
 * 设置页（二级页“不像表单”，用户 10-02“不要像表单”、c84a D0420-4；6c2b 10-03）：
 * - 先是 TA 的名片：头像、名字、简介那一句。简介平时就是一句话，点进去直接改；改了才出“保存简介”“不改了”，不常驻一个灰掉的保存按钮。
 * - 朋友圈两项：公开动态是一整行开关（整行能点，读屏读作开关），公开范围是三段选择。
 *   下面是「现在家人以外谁能看到」（AudienceSummary，PRIVACY-COPY-01）：只照后端 effective_audience 写，主页、名字和物种、照片、到访动态分开说，
 *   主页没公开而动态公开时显著提醒，并直达 TA 的主页看已经公开的动态（c84a REQ15 第 3 节）。
 * - 隐私说明（c84a D0020-1 / D0150-2）：不再有“TA 的回信方式”开关和“聊天时更懂你”运营状态卡（运营后台按能力和额度管）；
 *   外部服务参与内容处理照实告知：第一句是 c84a 原句，后面跟“查看内容使用说明”，去 /settings/data-use（DATA-USE-COPY-01）；
 *   第二句（私密通讯和叮嘱不公开、谁能看主页在朋友圈里设）照旧。旧链接 /settings#privacy 进来仍滚到这里。
 * - TA 的名片里、简介下面是「允许 TA 谈恋爱」（WIRE-01 A4，RomanceSetting.tsx）；原创居民、读不出来的整项不显示。
 * - 隐私说明下面是「我屏蔽的」（WIRE-01 A6，BlockedSection.tsx，SettingsPage 里）：列出来、能解除。
 * - 账号与退出登录放在最后（SettingsPage）：退出不常用，不再是首屏一大块红按钮。
 */
type SettingsOwner = { userId: string | null; petId: string | null; mode: string; session: ReturnType<typeof useServices>["session"]; client: QueryClient; serviceId: number; epoch: number };
type SettingsGuard = { owns: () => boolean; run: <T>(operation: () => Promise<T>) => Promise<T> };
const settingsSessionIds = new WeakMap<object, number>();
let nextSettingsService = 0;
let nextSettingsEpoch = 0;
class SettingsScopeEnded extends Error {
  constructor() { super("Settings context ended"); }
}
function newSettingsOwner(userId: string | null, petId: string | null, mode: string, session: SettingsOwner["session"], client: QueryClient): SettingsOwner {
  let serviceId = settingsSessionIds.get(session);
  if (serviceId === undefined) { serviceId = ++nextSettingsService; settingsSessionIds.set(session, serviceId); }
  return { userId, petId, mode, session, client, serviceId, epoch: ++nextSettingsEpoch };
}
function settingsRetry(client: QueryClient, guard: SettingsGuard, count: number, error: Error): boolean {
  if (error instanceof SettingsScopeEnded || !guard.owns()) return false;
  const retry = client.getDefaultOptions().queries?.retry;
  return typeof retry === "function" ? retry(count, error) : retry === true || count < (typeof retry === "number" ? retry : retry === false ? 0 : 3);
}

function SettingsForm({ view, petId, settingsKey, pet, guard }: { view: SettingsView; petId: string | null; settingsKey: readonly unknown[]; pet: HouseholdPetBrief | null; guard: SettingsGuard }) {
  const { session } = useServices();
  const queryClient = useQueryClient();
  const [bio, setBio] = useState(view.bio ?? "");
  const savedBio = view.bio ?? "";
  const bioChanged = bio.trim() !== savedBio;
  // 带 #privacy 进来（原来通讯器那句入口的链接；现在那句去内容使用说明页）：滚到隐私说明
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#privacy") return;
    const target = document.getElementById("privacy");
    if (target && typeof target.scrollIntoView === "function") target.scrollIntoView({ block: "start" });
  }, [hash]);
  const saving = useRef(false);
  const save = useMutation({
    // 简介、公开范围、公开动态都是这只宠物的：带上当前宠物（一家有两只时不带就 409 pet_required）。
    mutationFn: (patch: SettingsUpdateInput) => guard.run(() => session.updateSettings(patch, petId)),
    onSuccess: (next) => {
      if (!guard.owns()) return;
      queryClient.setQueryData(settingsKey, next);
      void queryClient.invalidateQueries({ queryKey: ["social"] });
    },
    onSettled: () => { if (guard.owns()) saving.current = false; },
  });
  const submitPatch = (patch: SettingsUpdateInput) => {
    if (saving.current || !guard.owns()) return;
    saving.current = true;
    save.mutate(patch);
  };
  // 没保存成的那一项在哪张卡，错误就写在哪张卡下面
  const bioFailed = save.isError && save.variables !== undefined && "bio" in save.variables;
  return (
    <>
      <Card className="ps-settings-pet">
        <div className="ps-settings-pet__who">
          {pet ? <PetAvatar petId={pet.pet_id} name={pet.name} species={pet.species} photoUrl={pet.photo_url} origin={pet.origin} size={56} /> : null}
          <div>
            <strong>{pet ? `${pet.name}的名片` : "TA 的名片"}</strong>
            <small>别人点开 TA 的主页，先看到这一句。</small>
          </div>
        </div>
        <form
          className="ps-settings-bio"
          onSubmit={(e) => {
            e.preventDefault();
            if (bioChanged) submitPatch({ bio: bio.trim() });
          }}
        >
          <label htmlFor="bio">TA 的简介</label>
          <input id="bio" className="ps-settings-bio__line" maxLength={120} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="写一句话介绍 TA" enterKeyHint="done" />
          {bioChanged ? (
            <div className="ps-settings-bio__actions">
              <Button type="submit" size="sm" variant="primary" loading={save.isPending}>
                保存简介
              </Button>
              <Button type="button" size="sm" variant="ghost" disabled={save.isPending} onClick={() => setBio(savedBio)}>
                不改了
              </Button>
            </div>
          ) : null}
        </form>
        {bioFailed ? <ErrorState error={save.error} /> : null}
        <RomanceSetting pet={pet} />
      </Card>
      <Card className="ps-settings-group">
        <div className="ps-section-title" style={{ marginTop: 0 }}>
          朋友圈
        </div>
        <label className="ps-switch-row ps-settings-switch">
          <span>
            <strong>TA 旅行到访后发公开动态（含途中照片）</strong>
            <small>关闭后新的到访不再公开；已发的动态可以在朋友圈里撤下。</small>
          </span>
          <input type="checkbox" role="switch" className="ps-switch" checked={view.public_posts} disabled={save.isPending} onChange={(e) => submitPatch({ public_posts: e.target.checked })} />
        </label>
        <div className="ps-settings-visibility">
          <span>TA 的主页给谁看</span>
          <div className="ps-segmented" role="group" aria-label="主页公开范围">
            {VISIBILITY.map((v) => (
              <ToggleChip key={v.id} pressed={view.profile_visibility === v.id} onToggle={() => submitPatch({ profile_visibility: v.id })}>
                {v.label}
              </ToggleChip>
            ))}
          </div>
        </div>
        <AudienceSummary audience={view.effective_audience} petId={petId} />
        {save.isError && !bioFailed ? <ErrorState error={save.error} /> : null}
      </Card>
      <Card id="privacy" className="ps-settings-privacy">
        <div className="ps-section-title" style={{ marginTop: 0 }}>
          隐私说明
        </div>
        <p>{CHAT_DATA_USE}</p>
        <DataUseLink />
        <p>你们的私聊和生活叮嘱不会公开；TA 的主页和到访动态给谁看，在上面“朋友圈”里设。</p>
      </Card>
    </>
  );
}

export function SettingsPage() {
  const { session } = useServices();
  const queryClient = useQueryClient();
  const { userId, pet } = useCurrentHousehold();
  const petId = pet?.pet_id ?? null;
  const [owner, setOwner] = useState(() => newSettingsOwner(userId, petId, env.dataMode, session, queryClient));
  const committed = useRef<SettingsOwner | null>(null);
  useLayoutEffect(() => {
    committed.current = owner;
    return () => { if (committed.current === owner) committed.current = null; };
  }, [owner]);
  if (owner.userId !== userId || owner.petId !== petId || owner.mode !== env.dataMode || owner.session !== session || owner.client !== queryClient) {
    setOwner(newSettingsOwner(userId, petId, env.dataMode, session, queryClient));
    return null;
  }
  return <OwnedSettingsPage key={owner.epoch} owner={owner} pet={pet ?? null} isCurrent={() => committed.current === owner} />;
}

function OwnedSettingsPage({ owner, pet, isCurrent }: { owner: SettingsOwner; pet: HouseholdPetBrief | null; isCurrent: () => boolean }) {
  const { session } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userId, petId } = owner;
  const fixture = owner.mode === "fixture";
  const confirmed = fixture || Boolean(userId?.trim());
  const mount = useRef({ active: false, generation: 0 });
  useLayoutEffect(() => {
    const lifetime = mount.current;
    lifetime.active = true;
    lifetime.generation++;
    return () => { lifetime.active = false; lifetime.generation++; };
  }, [owner]);
  const owns = (generation = mount.current.generation) => confirmed && mount.current.active && mount.current.generation === generation && isCurrent();
  const guard: SettingsGuard = {
    owns,
    run: async <T,>(operation: () => Promise<T>) => {
      const generation = mount.current.generation;
      if (!owns(generation)) throw new SettingsScopeEnded();
      try {
        const result = await operation();
        if (!owns(generation)) throw new SettingsScopeEnded();
        return result;
      } catch (error) {
        // Neutralize expired401 before shared Query/MutationCache auth hooks.
        if (!owns(generation)) throw new SettingsScopeEnded();
        throw error;
      }
    },
  };
  const settingsKey = [...(fixture ? queryKeys.settings : [...queryKeys.settings, userId ?? "-", petId ?? "-"]), "reader", owner.mode, owner.serviceId, owner.epoch];
  const settings = useQuery({ queryKey: settingsKey, queryFn: () => guard.run(() => session.settings(petId)), enabled: confirmed && (fixture || Boolean(petId)), retry: (count, error) => settingsRetry(queryClient, guard, count, error) });
  // This account reader is local to SettingsPage; every other global session
  // hook and the session base prefix/30s/API stay as before.
  const currentQuery = useQuery({
    queryKey: [...queryKeys.session, "settings-reader", userId ?? "-", owner.mode, owner.serviceId, owner.epoch],
    queryFn: () => guard.run(async () => {
      const result = await session.current();
      if (!fixture && result.authenticated && result.user?.user_id !== userId) throw new SettingsScopeEnded();
      return result;
    }),
    enabled: confirmed,
    staleTime: 30_000,
    retry: (count, error) => settingsRetry(queryClient, guard, count, error),
  });
  // No confirmed live identity: present the existing sign-in entry; no private
  // current/settings query or legacy cached account is adopted.
  const current = { ...currentQuery, isError: confirmed && currentQuery.isError, data: confirmed ? currentQuery.data : { authenticated: false, user: null } };
  const exiting = useRef(false);
  const logout = useMutation({
    mutationFn: () => guard.run(() => session.logout()),
    onSettled: () => {
      if (!guard.owns()) return;
      exiting.current = false;
      queryClient.clear();
      if (guard.owns()) navigate("/welcome", { replace: true });
    },
  });
  const submitLogout = () => {
    if (exiting.current || !guard.owns()) return;
    exiting.current = true;
    logout.mutate();
  };
  return (
    <Page>
      <TopBar title="账号与设置" back="/me" />
      <div className="ps-stack">
        {settings.isError ? <ErrorState error={settings.error} onRetry={() => void settings.refetch()} /> : settings.data ? <SettingsForm key={owner.epoch} view={settings.data} petId={petId} settingsKey={settingsKey} pet={pet ?? null} guard={guard} /> : <LoadingState lines={2} />}
        {/* 我屏蔽的（WIRE-01 A6）：在隐私说明下面；按账号读，不分是哪只宠物 */}
        <BlockedSection />
        <Card className="ps-settings-account">
          <div className="ps-section-title" style={{ marginTop: 0 }}>
            当前账号
          </div>
          {current.isError ? (
            <ErrorState error={current.error} onRetry={() => void current.refetch()} />
          ) : current.data ? (
            current.data.authenticated ? (
              <div className="ps-settings-account__row">
                <span className="ps-settings-account__icon" aria-hidden="true">
                  <Icon name="user" />
                </span>
                <span className="ps-settings-account__who">
                  <strong>{current.data.user?.username ?? current.data.user?.display_name ?? "已登录"}</strong>
                  <small>{current.data.user?.auth_method === "apple_bearer" ? "Apple 登录" : "网页账号"}</small>
                </span>
                <Button className="ps-settings-logout" variant="ghost" size="sm" loading={logout.isPending} onClick={submitLogout}>
                  退出登录
                </Button>
              </div>
            ) : (
              <div className="ps-stack">
                <span className="ps-muted">{env.dataMode === "fixture" ? "演示模式没有账号。" : "未登录。"}</span>
                <Link className="ps-btn ps-btn--secondary" to="/welcome">
                  去注册/登录
                </Link>
              </div>
            )
          ) : (
            <LoadingState lines={1} />
          )}
        </Card>
      </div>
    </Page>
  );
}
